import React, { Suspense, lazy, memo } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { AccountAccessRight } from "src/accounts/enums/AccountAccessRight";
import { ACCOUNT_LIST_PATH } from "src/accounts/views/AccountsPaths";
import { ApiSubscribersAccessRight } from "src/api-subscribers/enums/ApiSubscribersAccessRight";
import { API_SUBSCRIBERS_VIEW_PATH } from "src/api-subscribers/views/ApiSubscribersPaths";
import { DEFAULT_VIEW_PATH } from "src/app/ui/DefaultViewPath";
import AccessRightRoute from "src/authentication/components/AccessRightRoute";
import { useAccessible } from "src/authentication/hooks/useAccessible";
import { BillingsAccessRight } from "src/billings/enums/BillingsAccessRight";
import { BILLINGS_VIEW_PATH } from "src/billings/views/BillingsPath";
import ChatsAccessRight from "src/chats/enums/ChatsAccessRight";
import {
  CHATS_PATH,
  CHATS_STATUS_PATH,
  CHATS_STATUS_WITH_UUID_PATH,
  getChatsStatusPath,
} from "src/chats/views/ChatPaths";
import { CouponGroupAccessRight } from "src/coupon-groups/enums/AccountAccessRight";
import { COUPON_GROUPS_VIEW_PATH } from "src/coupon-groups/views/CouponGroupPaths";
import { CouponImportAccessRights } from "src/coupon-imports/enums/CouponImportAccessRights";
import { COUPON_IMPORT_LIST_PATH } from "src/coupon-imports/views/CouponImportPaths";
import { DispatchSubscriberExportsAccessRight } from "src/dispatch-subscriber-exports/enums/DispatchSubscriberExportsAccessRight";
import { DISPATCH_SUBSCRIBER_EXPORTS_LIST_PATH } from "src/dispatch-subscriber-exports/views/DispatchSubscriberExportsPaths";
import { DispatchSummaryExportsAccessRight } from "src/dispatch-summary-exports/enums/DispatchSummaryExportsAccessRight";
import { DISPATCH_SUMMARY_EXPORTS_LIST_PATH } from "src/dispatch-summary-exports/views/DispatchSummaryExportsPaths";
import { DISPATCHES_VIEW_PATH } from "src/dispatches/views/DispatchPaths";
import { HumanoidAccessRight } from "src/humanoid/enums/HumanoidAccessRight";
import { HUMANOID_VIEW_PATH } from "src/humanoid/views/HumanoidViewPaths";
import IntegrationAccessRight from "src/integrations/enums/IntegrationAccessRight";
import { INTEGRATIONS_LIST_PATH } from "src/integrations/views/IntegrationPaths";
import KeywordAccessRight from "src/keywords/enums/KeywordAccessRight";
import { KEYWORDS_VIEW_PATH } from "src/keywords/views/KeywordPaths";
import ListAccessRight from "src/lists/enums/ListAccessRight";
import CreateListView, {
  LIST_CREATE_VIEW_PATH,
} from "src/lists/views/CreateListView";
import ListsView, { LISTS_VIEW_PATH } from "src/lists/views/ListsView";
import { LookupAccessRight } from "src/lookup/enums/LookupAccessRight";
import { LOOKUP_VIEW_PATH } from "src/lookup/views/LookupViewPaths";
import { MANAGE_VIEW_PATH } from "src/manage/views/ManageView";
import MerchantGroupAccessRight from "src/merchantgroups/enums/MerchantGroupAccessRight";
import CreateMerchantGroupView, {
  CREATE_MERCHANT_GROUP_VIEW_PATH,
} from "src/merchantgroups/views/CreateMerchantGroupView";
import EditMerchantGroupView, {
  EDIT_MERCHANT_GROUP_VIEW_PATH,
} from "src/merchantgroups/views/EditMerchantGroupView";
import MerchantGroupDetailView from "src/merchantgroups/views/MerchantGroupDetailView";
import { MERCHANT_GROUP_DETAIL_VIEW_PATH } from "src/merchantgroups/views/MerchantGroupPaths";
import MerchantGroupsView, {
  MERCHANT_GROUPS_VIEW_PATH,
} from "src/merchantgroups/views/MerchantGroupsView";
import { MerchantAccessRight } from "src/merchants/enums/MerchantAccessRight";
import { MERCHANTS_VIEW_PATH } from "src/merchants/views/MerchantPaths";
import { NumberRequestAccessRight } from "src/number-requests/enums/NumberRequestAccessRight";
import { NUMBER_REQUESTS_PATH } from "src/number-requests/views/NumberRequestsPaths";
import { NUMBERS_VIEW_PATH } from "src/numbers/views/NumbersPaths";
import OfferAccessRight from "src/offers/enums/OfferAccessRight";
import { OFFERS_VIEW_PATH } from "src/offers/views/OfferPaths";
import { Loading } from "src/shared/components/Loading";
import SmartLinkAccessRight from "src/smartlinks/enums/SmartLinkAccessRight";
import { SMART_LINKS_VIEW_PATH } from "src/smartlinks/views/SmartLinksPath";
import { StoreGroupAccessRight } from "src/storeGroups/enums/StoreGroupAccessRight";
import { STORE_GROUPS_VIEW_PATH } from "src/storeGroups/views/StoreGroupPaths";
import StoreAccessRight from "src/stores/enums/StoreAccessRight";
import { STORES_VIEW_PATH } from "src/stores/views/StorePaths";
import { StoresImportAccessRight } from "src/stores-import/enums/StoresImportAccessRight";
import { STORES_IMPORT_LIST_PATH } from "src/stores-import/views/StoresImportPaths";
import SubscriberExportsAccessRight from "src/subscriber-exports/enums/SubscriberExportsAccessRight";
import { SUBSCRIBERS_EXPORT_LIST_PATH } from "src/subscriber-exports/views/SubscriberExportsPaths";
import SubscriberAccessRight from "src/subscribers/enums/SubscriberAccessRight";
import {
  SUBSCRIBERS_VIEW_PATH,
  SUBSCRIBER_DETAIL_VIEW_PATH,
} from "src/subscribers/views/SubscriberPaths";
import SubscribersImportAccessRight from "src/subscribers-import/enums/SubscribersImportAccessRight";
import { SUBSCRIBERS_IMPORT_LIST_PATH } from "src/subscribers-import/views/SubscribersImportPaths";
import { UnsubscribeImportAccessRights } from "src/unsubscribe-imports/enums/UnsubscribeImportAccessRights";
import { UNSUBSCRIBE_IMPORT_LIST_PATH } from "src/unsubscribe-imports/views/UnsubscribeImportPaths";
import { UserAccessRight } from "src/users/enums/UserAccessRight";
import { USERS_LIST_PATH } from "src/users/views/UsersPaths";

export const AuthenticatedRoutes = memo(() => {
  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        <Route
          exact
          path={DEFAULT_VIEW_PATH}
          component={lazy(() => import("src/dashboard/views/DashboardView"))}
        />
        {/* Dispatches */}
        <Route
          path={DISPATCHES_VIEW_PATH}
          component={lazy(() => import("src/dispatches/views/DispatchesView"))}
        />
        {useAccessible(HumanoidAccessRight.VIEW_HUMANOID) && (
          <Route
            exact
            path={HUMANOID_VIEW_PATH}
            component={lazy(() => import("src/humanoid/views/HumanoidView"))}
          />
        )}
        {useAccessible(LookupAccessRight.PERFORM_LOOKUP) && (
          <Route
            exact
            path={LOOKUP_VIEW_PATH}
            component={lazy(() => import("src/lookup/views/LookupView"))}
          />
        )}
        {/* Manage */}
        <Redirect
          exact
          from={MANAGE_VIEW_PATH}
          to={MERCHANT_GROUPS_VIEW_PATH}
        />
        {/* Merchant Groups */}
        <AccessRightRoute
          rights={MerchantGroupAccessRight.VIEW_MERCHANT_GROUPS}
          exact
          path={MERCHANT_GROUPS_VIEW_PATH}
          component={MerchantGroupsView}
        />
        <AccessRightRoute
          rights={MerchantGroupAccessRight.VIEW_MERCHANT_GROUPS}
          exact
          path={CREATE_MERCHANT_GROUP_VIEW_PATH}
          render={CreateMerchantGroupView}
        />
        <AccessRightRoute
          rights={MerchantGroupAccessRight.VIEW_MERCHANT_GROUPS}
          exact
          path={MERCHANT_GROUP_DETAIL_VIEW_PATH}
          render={MerchantGroupDetailView}
        />
        <AccessRightRoute
          rights={MerchantGroupAccessRight.VIEW_MERCHANT_GROUPS}
          exact
          path={EDIT_MERCHANT_GROUP_VIEW_PATH}
          render={EditMerchantGroupView}
        />
        {/* Merchants */}
        {useAccessible(MerchantAccessRight.VIEW_MERCHANTS) && (
          <Route
            path={MERCHANTS_VIEW_PATH}
            component={lazy(() => import("src/merchants/views/MerchantsView"))}
          />
        )}
        {/* Stores */}
        {useAccessible(StoreAccessRight.VIEW_STORES) && (
          <Route
            path={STORES_VIEW_PATH}
            component={lazy(() => import("src/stores/views/StoresView"))}
          />
        )}

        {/* Lists */}
        <AccessRightRoute
          rights={ListAccessRight.VIEW_LISTS}
          except={MerchantAccessRight.VIEW_MERCHANTS}
          exact
          path={LISTS_VIEW_PATH}
          component={ListsView}
        />
        <AccessRightRoute
          rights={ListAccessRight.VIEW_LISTS}
          except={MerchantAccessRight.VIEW_MERCHANTS}
          exact
          path={LIST_CREATE_VIEW_PATH}
          render={CreateListView}
        />
        {/* Store Groups */}
        {useAccessible(StoreGroupAccessRight.VIEW_STORE_GROUPS) && (
          <Route
            path={STORE_GROUPS_VIEW_PATH}
            component={lazy(
              () => import("src/storeGroups/views/StoreGroupsView"),
            )}
          />
        )}
        {/* Keywords */}
        {useAccessible(KeywordAccessRight.VIEW_KEYWORDS) && (
          <Route
            path={KEYWORDS_VIEW_PATH}
            component={lazy(() => import("src/keywords/views/KeywordsView"))}
          />
        )}
        {/* Offers */}
        {useAccessible(OfferAccessRight.VIEW_OFFERS) && (
          <Route
            path={OFFERS_VIEW_PATH}
            component={lazy(() => import("src/offers/views/OffersView"))}
          />
        )}
        {/* Coupon Groups */}
        {useAccessible(CouponGroupAccessRight.VIEW_COUPON_GROUPS) && (
          <Route
            path={COUPON_GROUPS_VIEW_PATH}
            component={lazy(
              () => import("src/coupon-groups/views/CouponGroupsView"),
            )}
          />
        )}
        {/* Smartlinks */}
        {useAccessible(SmartLinkAccessRight.VIEW_SMART_LINKS) && (
          <Route
            path={SMART_LINKS_VIEW_PATH}
            component={lazy(
              () => import("src/smartlinks/views/SmartLinksView"),
            )}
          />
        )}
        {/* Subscribers */}
        {useAccessible(SubscriberAccessRight.VIEW_SUBSCRIBERS) && (
          <Route
            exact
            path={SUBSCRIBERS_VIEW_PATH}
            component={lazy(
              () => import("src/subscribers/views/SubscribersView"),
            )}
          />
        )}
        {useAccessible(SubscriberAccessRight.VIEW_SUBSCRIBERS) && (
          <Route
            path={SUBSCRIBER_DETAIL_VIEW_PATH}
            component={lazy(
              () => import("src/subscribers/views/SubscriberDetailView"),
            )}
          />
        )}
        {/* Users */}
        {useAccessible(UserAccessRight.VIEW_USERS) && (
          <Route
            path={USERS_LIST_PATH}
            component={lazy(() => import("src/users/views/UsersView"))}
          />
        )}
        {/* Integrations */}
        {useAccessible(IntegrationAccessRight.VIEW_INTEGRATIONS) && (
          <Route
            path={INTEGRATIONS_LIST_PATH}
            component={lazy(
              () => import("src/integrations/views/IntegrationsView"),
            )}
          />
        )}
        {useAccessible(
          SubscribersImportAccessRight.VIEW_IMPORT_SUBSCRIBERS_LIST,
        ) && (
          <Route
            path={SUBSCRIBERS_IMPORT_LIST_PATH}
            component={lazy(
              () =>
                import(
                  "src/subscribers-import/views/SubscribersImportListView"
                ),
            )}
          />
        )}
        {useAccessible(
          SubscriberExportsAccessRight.VIEW_EXPORT_SUBSCRIBERS_LIST,
        ) && (
          <Route
            path={SUBSCRIBERS_EXPORT_LIST_PATH}
            component={lazy(
              () =>
                import(
                  "src/subscriber-exports/views/SubscriberExportsListsView"
                ),
            )}
          />
        )}
        {useAccessible(StoresImportAccessRight.VIEW_IMPORT_STORES) && (
          <Route
            path={STORES_IMPORT_LIST_PATH}
            component={lazy(
              () => import("src/stores-import/views/StoresImportListsView"),
            )}
          />
        )}
        {useAccessible(
          UnsubscribeImportAccessRights.VIEW_UNSUBSCRIBE_IMPORT,
        ) && (
          <Route
            path={UNSUBSCRIBE_IMPORT_LIST_PATH}
            component={lazy(
              () =>
                import(
                  "src/unsubscribe-imports/views/UnsubscribeImportsListView"
                ),
            )}
          />
        )}
        {useAccessible(CouponImportAccessRights.VIEW_COUPON_IMPORT) && (
          <Route
            path={COUPON_IMPORT_LIST_PATH}
            component={lazy(
              () => import("src/coupon-imports/views/CouponImportsListView"),
            )}
          />
        )}
        {useAccessible(
          DispatchSummaryExportsAccessRight.VIEW_EXPORT_DISPATCH_SUMMARIES_LIST,
        ) && (
          <Route
            path={DISPATCH_SUMMARY_EXPORTS_LIST_PATH}
            component={lazy(
              () =>
                import(
                  "src/dispatch-summary-exports/views/DispatchSummaryExportsView"
                ),
            )}
          />
        )}
        {useAccessible(
          DispatchSubscriberExportsAccessRight.VIEW_EXPORT_DISPATCH_SUBSCRIBERS_LIST,
        ) && (
          <Route
            path={DISPATCH_SUBSCRIBER_EXPORTS_LIST_PATH}
            component={lazy(
              () =>
                import(
                  "src/dispatch-subscriber-exports/views/DispatchSubscriberExportsView"
                ),
            )}
          />
        )}
        {useAccessible(AccountAccessRight.VIEW_ACCOUNTS) && (
          <Route
            path={NUMBERS_VIEW_PATH}
            component={lazy(() => import("src/numbers/views/NumbersView"))}
          />
        )}
        {useAccessible(AccountAccessRight.VIEW_ACCOUNTS) && (
          <Route
            path={ACCOUNT_LIST_PATH}
            component={lazy(() => import("src/accounts/views/AccountsView"))}
          />
        )}
        {useAccessible(NumberRequestAccessRight.VIEW_NUMBER_REQUESTS) && (
          <Route
            path={NUMBER_REQUESTS_PATH}
            component={lazy(
              () => import("src/number-requests/views/NumberRequestsView"),
            )}
          />
        )}

        {/* Api Subscribers */}
        {useAccessible(ApiSubscribersAccessRight.VIEW_API_SUBSCRIBERS) && (
          <Route
            path={API_SUBSCRIBERS_VIEW_PATH}
            component={lazy(
              () => import("src/api-subscribers/views/ApiSubscribersView"),
            )}
          />
        )}

        {/* Billings */}
        {useAccessible(BillingsAccessRight.VIEW_BILLINGS) && (
          <Route
            path={BILLINGS_VIEW_PATH}
            component={lazy(() => import("src/billings/views/BillingsView"))}
          />
        )}

        {/* Chats */}
        {useAccessible(ChatsAccessRight.VIEW_CHATS_LIST) && (
          <Redirect exact from={CHATS_PATH} to={getChatsStatusPath("OPEN")} />
        )}
        {useAccessible(ChatsAccessRight.VIEW_CHATS_LIST) && (
          <Route
            path={[CHATS_STATUS_WITH_UUID_PATH, CHATS_STATUS_PATH]}
            component={lazy(() => import("src/chats/views/ChatsView"))}
          />
        )}

        <Route component={lazy(() => import("src/misc/views/ErrorView"))} />
      </Switch>
    </Suspense>
  );
});

AuthenticatedRoutes.displayName = "AuthenticatedRoutes";
